import React from 'react'
import { createPortal } from 'react-dom'
import { Button, Heading, Text } from '../ui'
import { supportCenterAttendService } from '../../services/supportCenterAttend'
import type { SupportCenterAgendaEvent } from '../../types/supportCenterAppointments'
import {
  EMPTY_PSYCH_ATTENDANCE_FORM,
  SUPPORT_CENTER_PSYCH_EMOTIONAL_STATES,
  SUPPORT_CENTER_PSYCH_REFERRALS,
  SUPPORT_CENTER_PSYCH_RISK_LEVELS,
  type SupportCenterPsychAttendanceFormState,
  type SupportCenterPsychAttendancePayload,
  type SupportCenterPsychEmotionalState,
  type SupportCenterPsychReferral,
  type SupportCenterPsychRiskLevel,
} from '../../types/supportCenterPsychAttendance'

const inputClass =
  'w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-60'

const sectionClass =
  'rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-800/40 p-4 space-y-3'

type PsychologicalAttendFormProps = {
  appointment: SupportCenterAgendaEvent
  canSubmit: boolean
  forceReadOnly?: boolean
  onClose: () => void
  onSubmitted: () => void
  onDraftSaved?: () => void
  /** Rodapé fixo do modal. Os botões de ação são renderizados nele. */
  actionsHost: HTMLElement | null
}

function toPayload(
  appointmentId: string,
  form: SupportCenterPsychAttendanceFormState
): SupportCenterPsychAttendancePayload {
  if (!form.demand_notes.trim()) throw new Error('Informe a demanda trazida.')
  if (!form.intervention_notes.trim()) throw new Error('Informe a intervenção realizada.')
  if (!form.emotional_state) throw new Error('Selecione o estado emocional ao final.')
  if (!form.risk_indicator) throw new Error('Selecione o indicador de risco.')

  const referral =
    form.referral && form.referral !== 'Nenhum' ? form.referral : null

  return {
    appointment_id: appointmentId,
    demand_notes: form.demand_notes.trim(),
    intervention_notes: form.intervention_notes.trim(),
    patient_response: form.patient_response.trim() || null,
    emotional_state: form.emotional_state,
    risk_indicator: form.risk_indicator,
    referral,
    next_contact_date: form.next_contact_date.trim() || null,
    additional_notes: form.additional_notes.trim() || null,
  }
}

/** Rascunho parcial: sem validação dos campos obrigatórios. */
function toDraftPayload(
  appointmentId: string,
  form: SupportCenterPsychAttendanceFormState
): SupportCenterPsychAttendancePayload {
  const referral =
    form.referral && form.referral !== 'Nenhum' ? form.referral : null
  return {
    appointment_id: appointmentId,
    demand_notes: form.demand_notes.trim(),
    intervention_notes: form.intervention_notes.trim(),
    patient_response: form.patient_response.trim() || null,
    emotional_state: (form.emotional_state ||
      '') as SupportCenterPsychEmotionalState,
    risk_indicator: (form.risk_indicator || '') as SupportCenterPsychRiskLevel,
    referral,
    next_contact_date: form.next_contact_date.trim() || null,
    additional_notes: form.additional_notes.trim() || null,
  }
}

function recordToForm(
  row: Awaited<ReturnType<typeof supportCenterAttendService.getPsychologicalAttendance>>
): SupportCenterPsychAttendanceFormState {
  if (!row) return { ...EMPTY_PSYCH_ATTENDANCE_FORM }
  const emotional =
    SUPPORT_CENTER_PSYCH_EMOTIONAL_STATES.find((s) => s === row.emotional_state) ?? ''
  const risk = SUPPORT_CENTER_PSYCH_RISK_LEVELS.find((s) => s === row.risk_indicator) ?? ''
  const referralRaw = row.referral?.trim() ?? ''
  const referral = (
    SUPPORT_CENTER_PSYCH_REFERRALS as readonly string[]
  ).includes(referralRaw)
    ? (referralRaw as SupportCenterPsychReferral)
    : referralRaw
      ? ('Outro' as SupportCenterPsychReferral)
      : ''
  return {
    demand_notes: row.demand_notes ?? '',
    intervention_notes: row.intervention_notes ?? '',
    patient_response: row.patient_response ?? '',
    emotional_state: emotional,
    risk_indicator: risk,
    referral,
    next_contact_date: row.next_contact_date?.slice(0, 10) ?? '',
    additional_notes: row.additional_notes ?? '',
  }
}

export const PsychologicalAttendForm: React.FC<PsychologicalAttendFormProps> = ({
  appointment,
  canSubmit,
  forceReadOnly = false,
  onClose,
  onSubmitted,
  onDraftSaved,
  actionsHost,
}) => {
  const [form, setForm] = React.useState<SupportCenterPsychAttendanceFormState>(
    () => ({ ...EMPTY_PSYCH_ATTENDANCE_FORM })
  )
  const [submittedAt, setSubmittedAt] = React.useState<string | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [confirmKind, setConfirmKind] = React.useState<'draft' | 'send' | null>(null)

  /** Somente leitura após envio do formulário (submitted_at), não após finalizar o atendimento. */
  const readOnly = forceReadOnly || !!submittedAt
  const fieldsDisabled = readOnly || saving || loading || !canSubmit

  React.useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    setSaving(false)
    setConfirmKind(null)
    void supportCenterAttendService
      .getPsychologicalAttendance(appointment.id)
      .then((row) => {
        if (cancelled) return
        setForm(recordToForm(row))
        setSubmittedAt(row?.submitted_at ?? null)
      })
      .catch((e) => {
        if (cancelled) return
        setForm({ ...EMPTY_PSYCH_ATTENDANCE_FORM })
        setSubmittedAt(null)
        setError(e instanceof Error ? e.message : 'Não foi possível carregar o formulário.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [appointment.id])

  const requestDraftConfirm = () => {
    if (!canSubmit || saving || readOnly) return
    setError(null)
    setConfirmKind('draft')
  }

  const handleSaveDraft = async () => {
    if (!canSubmit || saving || readOnly) return
    setSaving(true)
    setError(null)
    try {
      const payload = toDraftPayload(appointment.id, form)
      await supportCenterAttendService.savePsychAttendanceDraft(payload)
      setConfirmKind(null)
      onDraftSaved?.()
      onClose()
    } catch (e) {
      setConfirmKind(null)
      setError(e instanceof Error ? e.message : 'Não foi possível salvar o rascunho.')
    } finally {
      setSaving(false)
    }
  }

  const requestSendConfirm = React.useCallback(() => {
    if (!canSubmit || saving || readOnly) return
    try {
      toPayload(appointment.id, form)
      setError(null)
      setConfirmKind('send')
    } catch (e) {
      setConfirmKind(null)
      setError(e instanceof Error ? e.message : 'Revise o formulário antes de concluir.')
    }
  }, [appointment.id, canSubmit, form, readOnly, saving])

  const handleSend = async () => {
    if (!canSubmit || saving || readOnly) return
    setSaving(true)
    setError(null)
    try {
      const payload = toPayload(appointment.id, form)
      await supportCenterAttendService.submitPsychAttendance(payload)
      setSubmittedAt(new Date().toISOString())
      setConfirmKind(null)
      onSubmitted()
    } catch (e) {
      setConfirmKind(null)
      setError(e instanceof Error ? e.message : 'Não foi possível concluir o formulário.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-4">
      <Text size="sm" color="muted">
        Documento sigiloso - uso exclusivo do profissional.
        {readOnly
          ? ' Formulário concluído: somente leitura.'
          : ' Salve o rascunho quando quiser; ao concluir, não será possível alterar.'}
      </Text>

      {loading ? (
        <Text size="sm" color="muted">
          Carregando formulário...
        </Text>
      ) : (
        <>
          <div className={sectionClass}>
            <Text
              size="xs"
              className="font-semibold uppercase tracking-wide text-primary-700 dark:text-primary-300"
            >
              Demanda trazida
            </Text>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                O que o(a) segurado(a) trouxe neste atendimento?{' '}
                {!readOnly && <span className="text-red-500">*</span>}
              </label>
              <textarea
                className={`${inputClass} min-h-[80px]`}
                value={form.demand_notes}
                onChange={(e) => setForm((prev) => ({ ...prev, demand_notes: e.target.value }))}
                disabled={fieldsDisabled}
                placeholder="Queixa, situação ou tema apresentado..."
              />
            </div>
          </div>

          <div className={sectionClass}>
            <Text
              size="xs"
              className="font-semibold uppercase tracking-wide text-primary-700 dark:text-primary-300"
            >
              Conduta e intervenção
            </Text>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Intervenção realizada {!readOnly && <span className="text-red-500">*</span>}
              </label>
              <textarea
                className={`${inputClass} min-h-[80px]`}
                value={form.intervention_notes}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, intervention_notes: e.target.value }))
                }
                disabled={fieldsDisabled}
                placeholder="Escuta, orientação, técnica utilizada..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Resposta do(a) segurado(a)
              </label>
              <textarea
                className={`${inputClass} min-h-[60px]`}
                value={form.patient_response}
                onChange={(e) => setForm((prev) => ({ ...prev, patient_response: e.target.value }))}
                disabled={fieldsDisabled}
                placeholder="Como recebeu a intervenção?"
              />
            </div>
          </div>

          <div className={sectionClass}>
            <Text
              size="xs"
              className="font-semibold uppercase tracking-wide text-primary-700 dark:text-primary-300"
            >
              Avaliação final
            </Text>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Estado emocional ao final {!readOnly && <span className="text-red-500">*</span>}
              </label>
              <div className="flex flex-wrap gap-2">
                {SUPPORT_CENTER_PSYCH_EMOTIONAL_STATES.map((state) => {
                  const active = form.emotional_state === state
                  return (
                    <button
                      key={state}
                      type="button"
                      disabled={fieldsDisabled}
                      onClick={() =>
                        setForm((prev) => ({
                          ...prev,
                          emotional_state: state as SupportCenterPsychEmotionalState,
                        }))
                      }
                      className={`rounded-full px-3 py-1.5 text-xs font-medium border transition-colors ${
                        active
                          ? 'bg-primary-600 border-primary-600 text-white'
                          : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:border-primary-400'
                      }`}
                    >
                      {state}
                    </button>
                  )
                })}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Indicador de risco {!readOnly && <span className="text-red-500">*</span>}
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {SUPPORT_CENTER_PSYCH_RISK_LEVELS.map((level) => {
                  const active = form.risk_indicator === level
                  return (
                    <button
                      key={level}
                      type="button"
                      disabled={fieldsDisabled}
                      onClick={() =>
                        setForm((prev) => ({
                          ...prev,
                          risk_indicator: level as SupportCenterPsychRiskLevel,
                        }))
                      }
                      className={`rounded-lg px-2 py-3 text-xs font-semibold border text-center transition-colors ${
                        active
                          ? 'bg-primary-600 border-primary-600 text-white'
                          : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:border-primary-400'
                      }`}
                    >
                      {level}
                    </button>
                  )
                })}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Encaminhamento
                </label>
                <select
                  className={inputClass}
                  value={form.referral}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      referral: e.target.value as SupportCenterPsychReferral | '',
                    }))
                  }
                  disabled={fieldsDisabled}
                >
                  <option value="">Selecione...</option>
                  {SUPPORT_CENTER_PSYCH_REFERRALS.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Próximo contato
                </label>
                <input
                  type="date"
                  className={inputClass}
                  value={form.next_contact_date}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, next_contact_date: e.target.value }))
                  }
                  disabled={fieldsDisabled}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Observações adicionais
              </label>
              <textarea
                className={`${inputClass} min-h-[60px]`}
                value={form.additional_notes}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, additional_notes: e.target.value }))
                }
                disabled={fieldsDisabled}
                placeholder="Combinados, alertas, contexto relevante..."
              />
            </div>
          </div>
        </>
      )}

      {error && (
        <div className="rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 px-3 py-2 text-sm">
          {error}
        </div>
      )}

      {actionsHost
        ? createPortal(
            <>
              <Button variant="ghost" onClick={onClose} disabled={saving}>
                {readOnly ? 'Fechar' : 'Cancelar'}
              </Button>
              {canSubmit && !readOnly && (
                <>
                  <Button variant="outline" onClick={requestDraftConfirm} disabled={saving || loading}>
                    Salvar rascunho
                  </Button>
                  <Button onClick={requestSendConfirm} disabled={saving || loading}>
                    Concluir
                  </Button>
                </>
              )}
            </>,
            actionsHost
          )
        : null}

      {confirmKind &&
        createPortal(
          <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6 space-y-4">
              <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                {confirmKind === 'draft' ? 'Salvar rascunho' : 'Concluir formulário'}
              </Heading>
              <Text size="sm" color="muted">
                {confirmKind === 'draft'
                  ? 'Ao confirmar, o rascunho será salvo e o formulário será fechado. Deseja continuar?'
                  : 'Após concluir, não será possível alterar o formulário. Deseja continuar?'}
              </Text>
              <div className="flex justify-end gap-3">
                <Button
                  variant="ghost"
                  onClick={() => setConfirmKind(null)}
                  disabled={saving}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={() => void (confirmKind === 'draft' ? handleSaveDraft() : handleSend())}
                  disabled={saving}
                >
                  {saving
                    ? confirmKind === 'draft'
                      ? 'Salvando...'
                      : 'Concluindo...'
                    : 'Confirmar'}
                </Button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  )
}
